import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import type { ResourceNode, SchedulerEvent, ViewMode } from '../../types';
import { startOfDay } from 'date-fns';
import { flattenResources } from '../utils/dateUtils';
import { useAppDispatch, useAppSelector } from '../../app/hooks';

interface SchedulerContextType {
  resources: ResourceNode[];
  flatResources: any[];
  events: SchedulerEvent[];
  viewMode: ViewMode;
  startDate: Date;
  endDate: Date;
  expandedResourceIds: Set<string>;
  toggleResource: (resource: ResourceNode) => void;
  handleEventUpdate: (updatedEvent: SchedulerEvent) => void;
  onEventClick?: (ev: SchedulerEvent) => void;
  onEventContextMenu?: (ev: SchedulerEvent, e: React.MouseEvent) => void;
  onResourceClick?: (resource: ResourceNode) => void;
  onMilestoneAdd?: (resource: any) => void;
  UpdateEventResize?: (resource: any) => void;
  onTimeOffClick?: (resource: any)=> any;
  isLoading: boolean;
  setIsLoading: (val: boolean) => void;
}

const SchedulerContext = createContext<SchedulerContextType | undefined>(undefined);

export const SchedulerProvider: React.FC<any> = ({ 
  children, 
  resources, 
  events: initialEvents, 
  viewMode = 'Month', 
  expandMode = 'multiple', // 'single' or 'multiple'
   startDate: propStartDate, // <--- 1. ADD THIS (Receive the prop)
  endDate: propEndDate,
  onEventUpdate, 
  onEventClick, 
  onEventContextMenu,
  onResourceClick,
  UpdateEventResize,
  onResourceExpand,
  onResourceCollapse,
  onMilestoneAdd,
  onTimeOffClick
}) => {
  const [events, setEvents] = useState(initialEvents);
  const {schedulerMonth, monthEndDate} = useAppSelector((state)=> state.scheduler);
  useEffect(() => { setEvents(initialEvents); }, [initialEvents]);

  const [expandedResourceIds, setExpandedResourceIds] = useState<Set<string>>(new Set());
   const startDate = useMemo(() => {
    if (propStartDate) return startOfDay(propStartDate);
    return startOfDay(new Date()); 
  }, [propStartDate]);

  const endDate = useMemo(() => {
    if (propEndDate) return startOfDay(propEndDate);
    const fallback = new Date(startDate);
    fallback.setDate(fallback.getDate() + 30);
    return fallback;
  }, [propEndDate, startDate]);
  const [isLoading, setIsLoading] = useState(false);

const toggleResource = (resource: any) => {
    const next = new Set(expandedResourceIds);
    const isExpanding = !next.has(resource.id);

    if (isExpanding) {
      if (expandMode === 'single' && resource.depth === 0) {
          next.clear();
      }
      next.add(resource.id);
      if (onResourceExpand) onResourceExpand(resource);
    } else {
      next.delete(resource.id);
      if (onResourceCollapse) onResourceCollapse(resource);
    }
    setExpandedResourceIds(next);
};
  const handleEventUpdate = (updatedEvent: SchedulerEvent) => {
    setEvents((prev: any) => prev.map((e: any) => e.id === updatedEvent.id ? updatedEvent : e));
    if (onEventUpdate) onEventUpdate(updatedEvent);
  };

  const flatResources = useMemo(() => 
    flattenResources(resources, expandedResourceIds), 
  [resources, expandedResourceIds]);


  return (
    <SchedulerContext.Provider value={{
      resources,
      flatResources,
      events,
      viewMode,
      startDate,
      endDate,
      expandedResourceIds,
      toggleResource,
      handleEventUpdate,
      UpdateEventResize,
      onEventClick,
      onEventContextMenu,
      onResourceClick,
      isLoading,
      setIsLoading,
      onMilestoneAdd,
      onTimeOffClick
    }}>
      {children}
    </SchedulerContext.Provider>
  );
};

export const useScheduler = () => {
  const context = useContext(SchedulerContext);
  if (!context) throw new Error("useScheduler must be used within SchedulerProvider");
  return context;
};